import type { SystemResourcesTranslationMessages } from './systemResourcesTranslationMessages';

export const systemResourcesLanguageFrench: SystemResourcesTranslationMessages = {
  domains: {
    name: 'Domaines',
    helperTexts: {
      assignDomainInput: 'Domaine dans lequel cet enregistrement doit être attribué',
      assignDomainArrayInput: 'Liste des domaines où cet enregistrement doit être attribué',
      organization: "Le nom de l'organisation",
      realm: 'Le domaine SIP',
      domainId: 'Domain id',
      license: 'Licence pour Domaine',
      altnames: {
        alias: 'Un alias de domaine/hôte',
      },
      metadata: {
        openai_api_key: 'Clé API requise pour accéder aux API d’OpenAI',
        realynx_api_token: 'Jeton requis pour accéder aux API de Realynx, utilisées pour l’analyse vocale IA',
        ms_contact_sync: {
          sync_config: {
            rule_value_list: 'Séparez plusieurs valeurs avec des virgules',
          },
        },
      },
    },
    notifications: {
      invalidRealm: 'Format de domaine invalide',
      invalidMicrosoftSyncRule: 'Complétez chaque règle ou supprimez-la',
    },
    fields: {
      fileUploadHint: 'Déposez un fichier .tar.gz ici, ou cliquez pour parcourir',
      id: 'ID',
      assigned_sip_profile: 'SIP Profile',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      altnames: 'Alias',
      enabled: 'Activé',
      enabled_features: 'Fonctionnalités activées',
      language: 'Langue',
      license: 'Licence',
      organization: 'Organisation',
      realm: 'Domaine', // or 'Royaume' or 'Secteur'
      metadata: {
        openai_api_key: 'Clé API OpenAI',
        obfuscated_openai_api_key: 'Clé API OpenAI (Masquée)',
        realynx_api_token: 'Jeton API Realynx',
        obfuscated_realynx_api_token: 'Jeton API Realynx (Masqué)',
        ms_contact_sync: {
          enabled: 'Synchronisation des contacts Microsoft activée',
          microsoft_config: {
            client_id: 'ID client',
            client_secret: 'Secret client',
            obfuscated_client_secret: 'Secret client (masqué)',
            realm: 'Domaine',
            tenant_id: 'ID du tenant',
          },
          sync_config: {
            active: 'Actif',
            add_rule: 'Ajouter une règle',
            contacts_filter: 'Filtre des contacts',
            enum_active: {
              any: 'Tous',
              false: 'Inactif',
              true: 'Actif',
            },
            enum_operator: {
              eq: 'Égale',
              in: 'Dans la liste',
            },
            enum_type: {
              all: 'Tous',
              contacts: 'Contacts',
              users: 'Utilisateurs',
            },
            ms_id_field: 'Champ ID MS',
            public_contacts_owner: 'Propriétaire des contacts publics',
            rule_field: 'Champ',
            rule_operator: 'Opérateur',
            rule_value: 'Valeur',
            type: 'Type de synchronisation',
            users_filter: 'Filtre des utilisateurs',
          },
        },
      },
    },
    operations: {
      edit: 'Modifier le domaine',
      create: 'Créer un domaine',
      delete: 'Supprimer un domaine',
      sections: {
        settings: 'Domaine configuration',
        metadata: 'Metadata',
        microsoftSync: 'Synchronisation Microsoft',
        license: 'Licence',
        backup: 'Sauvegarde',
      },
    },
    license: {
      serial: 'Numéro de série',
      version: 'Version',
      tenant: 'Tenant',
      issued_at: "Date d'émission",
      expiry: 'Expiration',
      resources: 'Ressources',
      users: 'Utilisateurs',
      extensions: 'Extensions',
      queues: "Files d'attente",
      carriers: 'Opérateurs',
      ivrs: 'IVR',
      voicemails: 'Messageries vocales',
      api_connectors: 'Connecteurs API',
      auth_providers: "Fournisseurs d'authentification",
      domains: 'Domaines',
      features: 'Fonctionnalités utilisateurs',
      features_queues: "Fonctionnalités files d'attente",
      agents: 'Agents',
      totp: 'TOTP',
      softphone_base: 'Softphone Base',
      softphone_premium: 'Softphone Premium',
      statistics: 'Statistiques',
      callback: 'Rappel',
      expiry_resources: 'Expiration des ressources',
      expiry_users: 'Expiration utilisateurs',
      expiry_queues: "Expiration files d'attente",
      metered_resources: 'Ressources mesurées',
      ai_calls_limit: 'Limite appels IA',
      ai_calls_reset_period: 'Période de réinitialisation',
      error: 'Erreur lors du chargement de la licence',
      noLicense: 'Aucune licence associée à ce domaine',
      yes: 'Oui',
      no: 'No',
    },
    backup: {
      startBackup: 'Démarrer la sauvegarde',
      success: 'Tâche de sauvegarde démarrée',
      error: 'Erreur lors du démarrage de la sauvegarde',
      button: 'Importer / Restaurer la sauvegarde',
      importTitle: 'Importer la sauvegarde du domaine',
      restoreTitle: 'Restaurer la sauvegarde du domaine',
      importMode: 'Importer',
      importModeDescription: "Crée un nouveau domaine à partir de l'archive de sauvegarde.",
      restoreMode: 'Restaurer',
      restoreModeDescription: "Écrase un domaine existant avec l'archive de sauvegarde.",
      realmOverride: 'Nouveau domaine (optionnel)',
      targetDomain: 'Domaine cible',
      importSuccess: "Tâche d'importation de sauvegarde démarrée",
      restoreSuccess: 'Tâche de restauration de sauvegarde démarrée',
      importError: "Erreur lors du démarrage de l'importation de la sauvegarde",
      restoreError: 'Erreur lors du démarrage de la restauration de la sauvegarde',
      apply: 'Appliquer',
    },
  },
  lcrs: {
    name: 'LCR',
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      rules: {
        id: 'ID',
        description: 'Description',
        priority: 'Priorité',
        enabled: 'Activé',
        assigned_carriers: 'Tronc',
        dialingcode: 'Indicatif téléphonique',
        matchrule: 'Règle de correspondance',
      },
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un LCR',
      edit: 'Modifier un LCR',
      delete: 'Supprimer un LCR',
      sections: {
        details: 'Détails',
        rules: 'Règles',
      },
    },
    helperTexts: {
      rules: {
        assigned_carriers: 'Une collection de transporteurs assignés',
        description: 'Notes aléatoires pour la règle',
        dialingcode: 'Préfixe à faire correspondre et à supprimer du numéro appelé',
        matchrule: 'Une expression régulière à comparer',
        priority: "Priorité de la règle, la plus basse l'emporte",
      },
    },
  },
  carriers: {
    name: 'Tronc SIP',
    fields: {
      id: 'ID',
      enabled: 'Activé',
      description: 'Description',
      name: 'Nom',
      mappings: 'Correspondances',
      parameters: {
        password: 'Mot de passe SIP',
        username: "Nom d'utilisateur SIP",
        realm: 'Domaine',
        enable_reg: "Activer l'enregistrement",
        expires: "Durée d'enregistrement",
        extension: "Numéro d'extension",
        host: 'Hôte',
        transport: 'Transport',
        from_user: 'Utilisateur dans From',
        from_domain: 'Domaine dans From',
        options_ping: 'Vérifiez auprès du SIP Options',
        callerid_in_from: 'Numéro appelant dans SIP From',
        p_asserted_identity: 'P-Asserted-Identity',
        p_preferred_identity: 'P-Preferred-Identity',
        p_called_party_id: 'P-Called-Party-ID',
        remote_party_id: 'Remote-Party-ID',
      },
      assigned_sip_profile: 'Profil SIP',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un tronc',
      edit: 'Modifier un tronc',
      delete: 'Supprimer un tronc',
      sections: {
        details: 'Détails',
        parameters: 'Paramètres',
        identity_headers: 'Identity headers',
      },
    },
    helperTexts: {
      mappings: 'Une collection de mappings de transporteurs',
      parameters: {
        host: 'Point de terminaison distant / Registrar',
        username:
          "Nom d'utilisateur d'authentification, utilisé également comme utilisateur de départ si l'option from_user n'est pas définie",
        password: "Mot de passe d'authentification",
        realm: "Domaine d'authentification, identique à celui de l'hôte s'il n'est pas défini",
        extension:
          "Nom d'enregistrement sur le point de terminaison distant, identique au nom d'utilisateur s'il n'est pas défini",
        from_user:
          "Nom d'utilisateur à utiliser dans from, identique au nom d'utilisateur, s'il n'est pas défini', from_user : 'Domaine à utiliser dans from, identique au nom d'utilisateur, si non défini",
        from_domain: "Domaine à utiliser à partir de, identique au domaine s'il est défini ou à l'hôte",
        transport: 'Protocole de transport',
        expires: "Délai d'expiration de l'enregistrement",
        enable_reg: "Activation de l'enregistrement à distance auprès du point d'accès",
        options_ping: "Activer la surveillance du point d'extrémité via des pings SIP OPTIONS",
        callerid_in_from: 'Utiliser le numéro de l’appelant dans l’en-tête SIP From',
        p_asserted_identity: "Configuration de l'en-tête P-Asserted-Identity envoyé sur les appels sortants",
        p_preferred_identity: "Configuration de l'en-tête P-Preferred-Identity envoyé sur les appels sortants",
        p_called_party_id: "Configuration de l'en-tête P-Called-Party-ID envoyé sur les appels sortants",
        remote_party_id: "Configuration de l'en-tête Remote-Party-ID envoyé sur les appels sortants",
      },
      identityHeaders: {
        parts: {
          user: "Partie utilisateur de l'en-tête",
          host: "Partie hôte de l'en-tête",
          display_name: "Nom affiché de l'en-tête, aucun nom affiché n'est envoyé s'il n'est pas défini",
        },
        customValues: {
          user: 'La valeur à envoyer',
          host: 'La valeur à envoyer',
          display_name: 'La valeur à envoyer',
        },
      },
    },
    identityHeaders: {
      parts: {
        user: 'Utilisateur',
        host: 'Hôte',
        display_name: 'Nom affiché',
      },
      customValues: {
        user: 'Utilisateur personnalisé',
        host: 'Hôte personnalisé',
        display_name: 'Nom affiché personnalisé',
      },
      preview: "En-tête résultant, les valeurs entre accolades sont résolues au moment de l'appel",
      sources: {
        none: 'Non défini',
        caller_id_name: "Nom de l'appelant",
        caller_id_number: "Numéro de l'appelant",
        called_id_name: "Nom de l'appelé",
        called_id_number: "Numéro de l'appelé",
        same_as_from: "Comme l'en-tête From",
        same_as_to: "Comme l'en-tête To",
        carrier_realm: 'Domaine du tronc',
        custom: 'Valeur personnalisée',
      },
    },
  },
  sip_profiles: {
    name: 'Profils SIP',
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      '100rel': '100rel',
      inbound_codecs: 'Codecs en Entrée',
      outbound_codecs: 'Codecs en Sortie',
      session_timers: 'Minuteries de Session',
      ice: 'ICE',
      rtp_profile: 'Profil RTP',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un profil',
      edit: 'Modifier un profil',
      delete: 'Supprimer un profil',
      sections: {
        details: 'Details',
        codecs: 'Codec',
      },
    },
    helperTexts: {
      '100rel': 'Activer la prise en charge de rfc3262',
      session_timers: 'Activer la prise en charge des temporisateurs de session SIP',
      ice: 'Prise en charge ICE. Requis pour les clients WebRTC. Peut ne pas être pris en charge par certains téléphones ou opérateurs.',
      rtp_profile:
        "Profil RTP à utiliser. Les profils RTP/SAVP(F) sont chiffrés. N'affecte pas les clients WSS/WebRTC (pour lesquels UDP/TLS/RTP/SAVPF est obligatoire).",
    },
  },
  ap_settings: {
    name: 'Paramètres',
  },
  ap_authentication: {
    name: 'Authentification',
  },
  ap_configurations: {
    name: 'Configurations',
    srtpValues: {
      disabled: 'Désactivé',
      optional: 'Optionnel',
      compulsory: 'Obligatoire',
    },
    helperTexts: {
      display_name:
        'Utilisez des doubles accolades pour les placeholders. Variables disponibles : {{lastname}}, {{firstname}}, {{extension}}.',
    },
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      settings: {
        admin_password: 'Mot de passe administrateur',
        ca_url: 'URL CA',
        call_waiting: 'Appel en attente',
        codecs: 'Codecs',
        display_method: "Méthode d'affichage",
        display_name: "Nom d'affichage",
        dscp_enable: 'Activer DSCP',
        dscp_signal: 'Signal DSCP',
        dscp_voice: 'Voix DSCP',
        fw_url: 'URL du firmware',
        ldap_base_search: 'Base de recherche LDAP',
        ldap_filter_name: 'Filtre LDAP nom',
        ldap_filter_number: 'Filtre LDAP numéro',
        ldap_host: 'Hôte LDAP',
        ldap_port: 'Port LDAP',
        ldap_title: 'Titre LDAP',
        mcast_addr: 'Adresses multicast',
        mcast_label: 'Libellés multicast',
        mwi_voicemail_number: 'Numéro de messagerie vocale MWI',
        mwi_voicemail_subscribe: 'Souscription MWI messagerie vocale',
        ntp_server_1: 'Serveur NTP 1',
        ntp_server_2: 'Serveur NTP 2',
        outbound_backup_proxy_port: 'Port SIP proxy sortant de secours',
        outbound_proxy_port: 'Port SIP proxy sortant',
        reg_expiry: "Expiration d'enregistrement",
        sip_port: 'Port SIP',
        sip_transport: 'Transport SIP',
        srtp: 'SRTP',
        vlan_pcport_enable: 'Activer VLAN port PC',
        vlan_pcport_vid: 'ID VLAN port PC',
        vlan_wanport_enable: 'Activer VLAN port WAN',
        vlan_wanport_vid: 'ID VLAN port WAN',
      },
    },
    groups: {
      details: 'Détails',
      labels: 'Étiquettes',
      sip_parameters: 'Paramètres SIP',
      voicemail_parameters: 'Paramètres de messagerie vocale',
      ntp_parameters: 'Paramètres NTP',
      generic_parameters: 'Paramètres généraux',
      vlan_parameters: 'Paramètres VLAN',
      dscp: 'DSCP',
      multicast_addresses: 'Adresses multicast',
      dsp_parameters: 'Paramètres DSP',
      firmware: 'Firmware',
      ldap_parameters: 'Paramètres LDAP',
    },
    operations: {
      create: 'Créer une configuration',
      delete: 'Supprimer une configuration',
      edit: 'Modifier une configuration',
      sections: {
        details: 'Détails',
        sip: 'SIP',
        device: 'Appareil',
        media: 'Média',
        directory: 'Annuaire,',
        network: 'Network',
      },
    },
  },
  ap_groups: {
    name: 'Groupes',
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      ap_configuration: "Configuration d'autoprovisioning",
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un groupe',
      delete: 'Supprimer un groupe',
      edit: 'Modifier un groupe',
      sections: {
        details: 'Détails',
      },
    },
  },
  ap_templates: {
    name: 'Modèles',
    fields: {
      id: 'ID',
      name: 'Nom',
      description: 'Description',
      markup: 'Balisage',
      metadata: {
        mac_regex: 'Regex MAC',
        max_accounts: 'Comptes max',
        ua_patterns: 'Motifs User-Agent',
      },
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un modèle',
      delete: 'Supprimer un modèle',
      edit: 'Modifier un modèle',
      sections: {
        details: 'Détails',
        metadata: 'Métadonnées',
      },
    },
    defaultTemplate: {
      button: 'Charger le modèle par défaut',
      dialogTitle: 'Charger le modèle par défaut',
      selectLabel: 'Modèle',
      warning: 'Cela écrasera le contenu actuel du champ de balisage.',
    },
    helperTexts: {
      ua_patterns: "Expression régulière comparée au User-Agent de l'appareil lors du provisionnement",
    },
    validation: {
      invalid_regex: 'Doit être une expression régulière valide',
    },
  },
  sip_devices: {
    name: 'Appareils SIP',
    fields: {
      id: 'ID',
      mac_address: 'Adresse MAC',
      description: 'Description',
      ap_group: "Groupe d'autoprovisioning",
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
      sip_identities: 'Identités SIP',
      provisioning_last_successful_at: 'Dernier provisioning réussi',
      provisioning_user_agent: 'Agent utilisateur de provisioning',
    },
    operations: {
      create: 'Créer un appareil SIP',
      delete: 'Supprimer un appareil SIP',
      edit: 'Modifier un appareil SIP',
      sections: {
        details: 'Détails',
      },
    },
  },
  scim_provisionings: {
    name: 'Provisioning SCIM',
    fields: {
      realm_id: 'ID de domaine',
      labels: 'Étiquettes',
    },
    sections: {
      settings: 'Paramètres',
      labels: 'Labels',
    },
    createDialogTitle: 'Créer un Token de Provisioning SCIM',
    createConfiguration: 'Créer une configuration',
    token: 'Token SCIM',
    enabled: 'Activé',
    deleteConfiguration: 'Supprimer la configuration',
    createSuccess: 'Token SCIM créé',
    createError: 'Erreur lors de la création du token SCIM',
    createConflictError: 'Un seul token SCIM par domaine est autorisé',
    deleteSuccess: 'Token SCIM supprimé',
    deleteError: 'Erreur lors de la suppression du token SCIM',
    updateError: 'Erreur lors de la mise à jour du token SCIM',
  },
  inbound_e164s: {
    name: 'Inbound E164s',
    fields: {
      id: 'ID',
      description: 'Description',
      e164_match: 'E164 Match',
      labels: { id: 'Étiquettes', name: 'Étiquettes' },
    },
    operations: {
      create: 'Créer un E164 entrant',
      delete: "Supprimer l'E164 entrant",
      edit: "Modifier l'E164 entrant",
      sections: {
        settings: 'Configuration des E164 entrants',
      },
    },
  },
};
